import { useTranslation } from '@shared/core';
import globalWindow from '@shared/core/globals';
import { isPast } from 'date-fns';
import { useRef, useState, useEffect, useMemo } from 'react';

/**
 * Countdown format options
 */
export const COUNTDOWN_FORMAT_OPTIONS = {
  DAYS_HOURS_MINUTES_SECONDS: 'daysHoursMinutesSeconds',
  DAYS_HOURS_MINUTES: 'daysHoursMinutes',
  DAYS_HOURS: 'daysHours',
  DAYS_ONLY: 'daysOnly',
} as const;

export type CountdownFormat = (typeof COUNTDOWN_FORMAT_OPTIONS)[keyof typeof COUNTDOWN_FORMAT_OPTIONS];

export interface TimeDifference
  extends Readonly<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  }> {}

export function getTimeDifference(date1: Date, date2: Date): TimeDifference {
  // Convert both dates to milliseconds
  const diffMs = Math.abs(date2.getTime() - date1.getTime());

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diffMs / (1000 * 60)) % 60);
  const seconds = Math.floor((diffMs / 1000) % 60);

  return { days, hours, minutes, seconds };
}

type TranslationFn = (strings: TemplateStringsArray, ...values: unknown[]) => string;

/**
 * Format countdown duration based on format type
 */
export function formatCountdownDuration(countdown: TimeDifference, format: CountdownFormat = COUNTDOWN_FORMAT_OPTIONS.DAYS_HOURS_MINUTES, tq: TranslationFn): string {
  const { days, hours, minutes, seconds } = countdown;

  switch (format) {
    case COUNTDOWN_FORMAT_OPTIONS.DAYS_ONLY:
      return tq`${days} days`;
    case COUNTDOWN_FORMAT_OPTIONS.DAYS_HOURS:
      return tq`${days} days ${hours} hrs`;
    case COUNTDOWN_FORMAT_OPTIONS.DAYS_HOURS_MINUTES_SECONDS:
      return tq`${days} days ${hours} hrs ${minutes} mins ${seconds} secs`;
    case COUNTDOWN_FORMAT_OPTIONS.DAYS_HOURS_MINUTES:
    default:
      return tq`${days} days ${hours} hrs ${minutes} mins`;
  }
}

const TimeDifferenceDefault: TimeDifference = { days: 0, hours: 0, minutes: 0, seconds: 0 } as const;

/** Poll every 250ms when displaying seconds so the seconds digit stays accurate despite timer drift, 10s otherwise */
function getIntervalMs(format: CountdownFormat | undefined): number {
  return format === COUNTDOWN_FORMAT_OPTIONS.DAYS_HOURS_MINUTES_SECONDS ? 250 : 10000;
}

function isSameTime(a: TimeDifference, b: TimeDifference): boolean {
  return a.days === b.days && a.hours === b.hours && a.minutes === b.minutes && a.seconds === b.seconds;
}

export const useCountdown = (milliseconds: number = 0, format?: CountdownFormat) => {
  const hasPassedRef = useRef<boolean>(isPast(milliseconds));
  const [countDown, setCountdown] = useState<TimeDifference>(() => getTimeDifference(new Date(milliseconds), new Date()) || TimeDifferenceDefault);
  const { tq } = useTranslation('joykit');

  useEffect(() => {
    const intervalMs = getIntervalMs(format);
    const interval = globalWindow.setInterval(() => {
      const wasPast = hasPassedRef.current;
      hasPassedRef.current = isPast(milliseconds);
      const passedChanged = wasPast !== hasPassedRef.current;
      const next = getTimeDifference(new Date(milliseconds), new Date());
      // Re-render only when the displayed value changes — but always on the future→past flip, since
      // hasPassed (a ref) is read during render and getTimeDifference can return the same value across it
      setCountdown(prev => (!passedChanged && isSameTime(prev, next) ? prev : next));
    }, intervalMs);
    return function cleanup() {
      globalWindow.clearInterval(interval);
    };
  }, [milliseconds, format]);

  const hasPassed = hasPassedRef.current;

  const formattedDuration = useMemo((): string => {
    const duration = formatCountdownDuration(countDown, format, tq);
    return hasPassed ? tq`${duration} ago` : duration;
  }, [format, hasPassed, countDown, tq]);

  return {
    formattedDuration,
    countDown,
  };
};
